"use client";

import React from "react";
import { Check } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function TheSalwaCareAdvantage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    title: "Understand Your Diagnosis",
    description: "Gain a clear understanding of your PSA trends, biopsy results, imaging, staging, and overall risk so you know exactly where you stand."
  }, {
    title: "Evaluate All Treatment Options",
    description: "Compare appropriate paths including active surveillance, surgery, radiation, focal therapy, and systemic treatments."
  }, {
    title: "Incorporate Advanced Testing",
    description: "When appropriate, evaluate genomic and germline testing to further refine risk and guide decision making."
  }, {
    title: "Align with Your Priorities",
    description: "Consider quality of life, side effects, long term outcomes, and your personal goals so the plan fits you, not just the diagnosis."
  }, {
    title: "Move Forward with Confidence",
    description: "Leave with greater clarity, direction, and confidence in the next step that feels right for you."
  }];
  return <section data-section-id="10350" id="the-path-to-informed-decisions" className="dark bg-background text-foreground relative overflow-hidden py-12" style={{
    "--background": "hsl(215 25% 12%)",
    "--foreground": "hsl(0 0% 100%)"
  } as React.CSSProperties}>
      <div ref={ref} className={cn("container mx-auto px-4 relative z-10 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className={cn("space-y-4 transition-all duration-700 delay-100", isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0")}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              A Clear Path Forward
            </h2>

            <p className="text-lg md:text-xl opacity-90 leading-relaxed font-sans tracking-wide">
              Navigating a prostate cancer diagnosis can feel overwhelming,
              especially when you are presented with complex information and
              multiple treatment options. My role is to guide you through a clear,
              step by step process so you can move from uncertainty to confident
              decision making.
            </p>

            <p className="text-lg md:text-xl opacity-90 leading-relaxed font-sans tracking-wide">
              Together, we review your diagnosis, evaluate your options, consider
              whether advanced testing may be useful, and align each decision with
              your quality of life goals and personal priorities.
            </p>
          </div>

          <div className="lg:pl-10">
            <ul className="space-y-4">
              {features.map((feature, i) => <li key={i} data-index={i} className={cn("flex items-start gap-5 transition-all duration-700", isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")} style={{
              transitionDelay: `${200 + i * 100}ms`
            }}>
                  <div className="shrink-0 mt-0">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/10 backdrop-blur-md border border-emerald-500/20 text-emerald-400 shadow-lg">
                      <Check className="w-6 h-6" strokeWidth={3} />
                    </div>
                  </div>

                  <div>
                    <h3 className="font-sans text-xl font-bold mb-2 tracking-tight">
                      {feature.title}
                    </h3>
                    <p className="opacity-80 font-sans leading-relaxed tracking-wide">
                      {feature.description}
                    </p>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}